import { useCallback, useEffect, useRef, useState } from 'react';

// Delay touch dragging until movement intent is clear. A stationary hold never
// reaches the map's drag callbacks, so it cannot spend movement or select a unit.
export default function useMapCombatShortcuts({ enabled, onToggle, onDown, onMove, onUp, onCancel, onTap }) {
  const [menu, setMenu] = useState(null);
  const [busy, setBusy] = useState(false);
  const gesture = useRef(null), suppressed = useRef(null), touches = useRef(new Set());
  const current = useRef({});
  current.current = { enabled, onToggle, onDown, onMove, onUp, onCancel, onTap };
  const clear = useCallback(() => {
    const state = gesture.current;
    if (state) clearTimeout(state.timer);
    gesture.current = null;
    return state;
  }, []);
  const cancel = useCallback(() => {
    const state = clear();
    if (state?.dragging) current.current.onCancel(state.start);
    if (state) suppressed.current = state.token.characterId;
  }, [clear]);
  useEffect(() => {
    const down = event => {
      if (!event.target.closest?.('[data-combat-unit-menu]')) setMenu(null);
      if (event.pointerType !== 'touch') return;
      touches.current.add(event.pointerId);
      if (touches.current.size > 1) { cancel(); setMenu(null); }
    };
    const forgetTouch = event => touches.current.delete(event.pointerId);
    const end = event => {
      touches.current.delete(event.pointerId);
      if (gesture.current?.start.pointerId === event.pointerId) cancel();
    };
    const multiTouch = event => { if (event.touches?.length > 1) { cancel(); setMenu(null); } };
    const key = event => { if (event.key === 'Escape') { cancel(); setMenu(null); } };
    document.addEventListener('pointerdown', down, true);
    document.addEventListener('pointerup', forgetTouch, true);
    document.addEventListener('pointercancel', forgetTouch, true);
    document.addEventListener('pointerup', end);
    document.addEventListener('pointercancel', end);
    document.addEventListener('touchstart', multiTouch, true);
    document.addEventListener('keydown', key);
    window.addEventListener('blur', cancel);
    return () => {
      clear();
      document.removeEventListener('pointerdown', down, true);
      document.removeEventListener('pointerup', forgetTouch, true);
      document.removeEventListener('pointercancel', forgetTouch, true);
      document.removeEventListener('pointerup', end);
      document.removeEventListener('pointercancel', end);
      document.removeEventListener('touchstart', multiTouch, true);
      document.removeEventListener('keydown', key);
      window.removeEventListener('blur', cancel);
    };
  }, [cancel, clear]); // Handlers read current callbacks rather than stale combat snapshots.
  useEffect(() => { if (!enabled) { cancel(); setMenu(null); } }, [enabled, cancel]);
  const toggle = async id => {
    if (!current.current.enabled || busy) return;
    setBusy(true);
    try { if (await current.current.onToggle(id)) setMenu(null); }
    finally { setBusy(false); }
  };
  const down = (event, token) => {
    if (!current.current.enabled) return false;
    if (event.button === 1) {
      event.preventDefault(); event.stopPropagation();
      suppressed.current = token.characterId;
      void toggle(token.characterId);
      return true;
    }
    if (event.pointerType !== 'touch') { suppressed.current = null; return false; }
    if (touches.current.size > 1 || event.touches?.length > 1) return true;
    cancel(); suppressed.current = null; setMenu(null);
    event.preventDefault(); event.stopPropagation();
    const start = { clientX: event.clientX, clientY: event.clientY, pointerId: event.pointerId,
      button: 0, pointerType: 'touch', currentTarget: event.currentTarget,
      preventDefault() {}, stopPropagation() {} };
    const state = { token, start, dragging: false };
    state.timer = setTimeout(() => {
      if (gesture.current !== state) return;
      suppressed.current = token.characterId;
      setMenu({ id: token.characterId, label: token.label || 'Unit', x: start.clientX, y: start.clientY });
      gesture.current = null;
    }, 500);
    gesture.current = state;
    try { event.currentTarget.setPointerCapture?.(event.pointerId); } catch { /* Touch fallback has no capture. */ }
    return true;
  };
  const move = event => {
    const state = gesture.current;
    if (!state || state.start.pointerId !== event.pointerId) return false;
    if (!state.dragging && Math.hypot(event.clientX-state.start.clientX, event.clientY-state.start.clientY) > 8) {
      clearTimeout(state.timer); state.dragging = true;
      current.current.onDown(state.start, state.token);
    }
    if (state.dragging) current.current.onMove(event);
    return true;
  };
  const up = (event, cancelled = false) => {
    const state = gesture.current;
    if (!state || state.start.pointerId !== event.pointerId) return false;
    clear();
    if (state.dragging) {
      suppressed.current = state.token.characterId;
      current.current[cancelled ? 'onCancel' : 'onUp'](event);
    } else {
      suppressed.current = state.token.characterId;
      if (!cancelled) current.current.onTap?.(state.token);
    }
    return true;
  };
  const blockClick = (event, id) => {
    if (suppressed.current !== id) return false;
    event.preventDefault(); event.stopPropagation(); return true;
  };
  return { menu, busy, cancel, close: () => setMenu(null), toggle, down, move, up, blockClick,
    context: (event, id) => enabled && (menu?.id === id || gesture.current?.token.characterId === id) };
}
